import { Badge, Card, MUTED_TEXT, Spinner, EmptyState, stackStyle } from "@dayvise/react-ui";
import { useQuery } from "@tanstack/react-query";
import { useDayviseClient } from "./providers.tsx";

/**
 * Real Git worktrees on this conversation (CMD-110/GIT-10): derived by the
 * server from the durable transcript of git.worktree/git.inspect results, so
 * the UI shows the actual lifecycle (create/select/retire, branch, head,
 * state) without running git or parsing transcripts.
 */
export function WorktreesStrip({ conversationId, branchId }: { conversationId: string; branchId: string | null }) {
  const client = useDayviseClient();
  const worktrees = useQuery({
    queryKey: ["worktrees", conversationId, branchId ?? "default"] as const,
    queryFn: () => client.worktrees.list(conversationId, branchId ? { branchId } : undefined),
  });
  const rows = worktrees.data?.worktrees ?? [];
  const active = rows.filter((row) => row.state !== "removed" && row.state !== "pruned");

  return (
    <Card
      title="Git worktrees"
      subtitle="Real isolated checkouts the run's git tools created on this conversation. Creating or selecting one never publishes anything; publishing and merging go through the same conflict-checked paths as file versions."
    >
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        {worktrees.isPending ? <Spinner label="Reading worktrees" /> : null}
        {worktrees.error ? <p role="alert" style={{ color: "var(--dayvise-color-danger)" }}>{worktrees.error instanceof Error ? worktrees.error.message : "Could not read worktrees"}</p> : null}
        {!worktrees.isPending && rows.length === 0 ? (
          <EmptyState title="No worktrees yet" description="Worktrees the run creates with its git tools appear here with their branch and head." />
        ) : null}
        {rows.map((row) => (
          <div key={row.name} style={stackStyle("var(--dayvise-space-xxs)")}>
            <div style={{ display: "flex", gap: "var(--dayvise-space-xs)", alignItems: "center", flexWrap: "wrap" }}>
              <strong>{row.name}</strong>
              <Badge tone={row.state === "removed" || row.state === "pruned" ? "neutral" : "success"}>{row.state}</Badge>
              <span style={MUTED_TEXT}>{row.branch}{row.head ? ` · ${row.head.slice(0, 10)}` : ""}</span>
              {row.ahead !== null || row.behind !== null ? (
                <Badge tone={row.behind ? "warning" : "neutral"}>
                  {row.ahead ?? 0} ahead{row.behind ? ` · ${row.behind} behind` : ""}
                </Badge>
              ) : null}
              {row.dirty === true ? <Badge tone="warning">{row.changedCount ?? row.changed.length} uncommitted</Badge> : null}
            </div>
            {row.path ? <p style={{ ...MUTED_TEXT, margin: 0 }}>{row.path}</p> : null}
            {row.diffFiles.length > 0 ? (
              <p style={{ ...MUTED_TEXT, margin: 0 }}>
                Diff: {row.diffFiles.slice(0, 8).join(", ")}{row.diffFiles.length > 8 ? ` +${row.diffFiles.length - 8} more` : ""}
              </p>
            ) : null}
            {row.changed.length > 0 && row.diffFiles.length === 0 ? (
              <p style={{ ...MUTED_TEXT, margin: 0 }}>
                Changed: {row.changed.slice(0, 8).join(", ")}{row.changed.length > 8 ? ` +${row.changed.length - 8} more` : ""}
              </p>
            ) : null}
          </div>
        ))}
        {active.length > 1 ? <p style={{ ...MUTED_TEXT, margin: 0 }}>{active.length} worktrees are active; each is a separate checkout on its own branch.</p> : null}
      </div>
    </Card>
  );
}
